/**
 * @voltxampere/spice — Tokenizer
 *
 * SPICE3 tokenization is line-oriented and almost exclusively
 * whitespace-separated. The lexer's job is to:
 *
 *   1. Split each line into bare tokens (identifiers, numbers,
 *      operator symbols, parens, equals, commas).
 *   2. Track absolute line / column positions for error messages.
 *   3. Strip whole-line comments (`* ...`) and inline comments
 *      (`; ...`) before tokenization.
 *   4. Honour SPICE line continuation: a leading `+` at column 0
 *      joins the line to its predecessor.
 *
 * The lexer does NOT classify identifiers vs. element letters or
 * model names — that's the parser's responsibility. It only emits
 * raw tokens with a coarse type tag.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const RE_NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?[a-zA-Z]*/;
const RE_WORD = /^[a-zA-Z_][\w./-]*/;
const RE_DIRECTIVE = /^\.[a-zA-Z]\w*/;
/**
 * Tokenize a single line into raw tokens. Inline comments (`;`)
 * are stripped and surfaced as a separate COMMENT token at the
 * end. Whitespace separates tokens but is otherwise discarded.
 */
function tokenizeLineRaw(text, line) {
    const tokens = [];
    let col = 1;
    let cursor = 0;
    while (cursor < text.length) {
        const ch = text[cursor];
        if (ch === undefined)
            break;
        // Whitespace (space, tab, CR — LF is line boundary, handled by caller).
        if (ch === " " || ch === "\t" || ch === "\r") {
            cursor += 1;
            col += 1;
            continue;
        }
        // Inline comment — gobble rest of line.
        if (ch === ";") {
            const rest = text.slice(cursor + 1).trim();
            tokens.push({ type: "COMMENT", value: rest, line, col });
            break;
        }
        // Single-char punctuation.
        if (ch === "(") {
            tokens.push({ type: "LPAREN", value: "(", line, col });
            cursor += 1;
            col += 1;
            continue;
        }
        if (ch === ")") {
            tokens.push({ type: "RPAREN", value: ")", line, col });
            cursor += 1;
            col += 1;
            continue;
        }
        if (ch === "=") {
            tokens.push({ type: "EQ", value: "=", line, col });
            cursor += 1;
            col += 1;
            continue;
        }
        if (ch === ",") {
            tokens.push({ type: "COMMA", value: ",", line, col });
            cursor += 1;
            col += 1;
            continue;
        }
        // String literal — `"...quoted..."`.
        if (ch === '"') {
            const endIdx = text.indexOf('"', cursor + 1);
            if (endIdx === -1) {
                // Unterminated — gobble rest of line as string body.
                tokens.push({
                    type: "STRING",
                    value: text.slice(cursor + 1),
                    line,
                    col,
                });
                break;
            }
            tokens.push({
                type: "STRING",
                value: text.slice(cursor + 1, endIdx),
                line,
                col,
            });
            const consumed = endIdx - cursor + 1;
            cursor += consumed;
            col += consumed;
            continue;
        }
        // Directive — `.foo` (one token, dot included). Must match
        // BEFORE number, because `.5` is a number but `.tran` is a word.
        const slice = text.slice(cursor);
        const dirMatch = RE_DIRECTIVE.exec(slice);
        if (dirMatch !== null) {
            const matched = dirMatch[0];
            tokens.push({ type: "WORD", value: matched, line, col });
            cursor += matched.length;
            col += matched.length;
            continue;
        }
        // Number — must start with a digit, sign, or `.`.
        const numMatch = RE_NUMBER.exec(slice);
        const wordMatch = RE_WORD.exec(slice);
        // Prefer NUMBER if it matches AND starts with digit/sign-digit/dot-digit
        // (avoid matching `R10` as a number — it's a word starting with R).
        if (numMatch !== null && (/^\d/.test(slice) || /^[+-]\d/.test(slice) || /^\.\d/.test(slice))) {
            const matched = numMatch[0];
            tokens.push({ type: "NUMBER", value: matched, line, col });
            cursor += matched.length;
            col += matched.length;
            continue;
        }
        if (wordMatch) {
            const matched = wordMatch[0];
            tokens.push({ type: "WORD", value: matched, line, col });
            cursor += matched.length;
            col += matched.length;
            continue;
        }
        // Unknown character — emit as a single-char WORD so the parser
        // can decide whether to error. SPICE files in the wild rarely
        // hit this branch.
        tokens.push({ type: "WORD", value: ch, line, col });
        cursor += 1;
        col += 1;
    }
    return tokens;
}
/**
 * Walk the source text into logical lines. Continuation lines
 * (leading `+` at column 0) get folded into the preceding line.
 * Whole-line comments (`* ...`) become standalone logical lines
 * with `isComment: true`.
 */
export function lex(source) {
    const lines = source.split(/\r?\n/);
    const out = [];
    let pendingTokens = null;
    let pendingLine = 0;
    let pendingRaw = [];
    const flushPending = () => {
        if (pendingTokens === null)
            return;
        out.push({
            line: pendingLine,
            tokens: pendingTokens,
            isComment: false,
            rawText: pendingRaw.join("\n"),
        });
        pendingTokens = null;
        pendingRaw = [];
        pendingLine = 0;
    };
    for (let i = 0; i < lines.length; i++) {
        const text = lines[i] ?? "";
        const lineNo = i + 1;
        // Whole-line comment `* ...`.
        if (text.startsWith("*")) {
            flushPending();
            out.push({
                line: lineNo,
                tokens: [
                    {
                        type: "COMMENT",
                        value: text.slice(1).trim(),
                        line: lineNo,
                        col: 1,
                    },
                ],
                isComment: true,
                rawText: text,
            });
            continue;
        }
        // Continuation line `+ ...`.
        if (text.startsWith("+")) {
            // Pretend the `+` is whitespace for tokenization purposes.
            const continuation = " " + text.slice(1);
            const more = tokenizeLineRaw(continuation, lineNo);
            if (pendingTokens === null) {
                // Continuation without a preceding line — treat as fresh.
                pendingTokens = more;
                pendingLine = lineNo;
                pendingRaw = [text];
            }
            else {
                pendingTokens = [...pendingTokens, ...more];
                pendingRaw.push(text);
            }
            continue;
        }
        // Skip blank lines without flushing — SPICE allows blank lines
        // between cards.
        if (text.trim().length === 0) {
            flushPending();
            continue;
        }
        // Regular line — flush any pending continuation buffer first.
        flushPending();
        pendingTokens = tokenizeLineRaw(text, lineNo);
        pendingLine = lineNo;
        pendingRaw = [text];
    }
    flushPending();
    return out;
}
//# sourceMappingURL=lexer.js.map